import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSchedules, useAgentSchedules, useUpdateSchedule, useDeleteSchedule } from '../../hooks/useSchedules';
import { useAgents } from '../../hooks/useAgents';
import { useConversations } from '../../hooks/useConversations';
import { Badge, Button, ConfirmModal, Select, getStatusVariant } from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import CreateScheduleModal from '../../components/schedules/CreateScheduleModal';
import { routes } from '../../lib/routes';
import type { Schedule } from '../../types';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatExpression(schedule: Schedule): string {
  if (schedule.scheduleType === 'one_time') {
    return formatDate(schedule.scheduleExpression);
  }
  return schedule.scheduleExpression;
}

interface ScheduleListProps {
  agentId?: string;
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

export default function ScheduleList({ agentId }: ScheduleListProps) {
  const { success, error: showError } = useToast();

  // Use agent-scoped or global query depending on prop
  const globalQuery = useSchedules(agentId ? undefined : {});
  const agentQuery = useAgentSchedules(agentId);
  const { data, isLoading } = agentId ? agentQuery : globalQuery;

  const { data: agentsData } = useAgents();
  const { data: conversationsData } = useConversations();
  const updateSchedule = useUpdateSchedule();
  const deleteSchedule = useDeleteSchedule();

  const [statusFilter, setStatusFilter] = useState('all');
  const [createModal, setCreateModal] = useState(false);
  const [editSchedule, setEditSchedule] = useState<Schedule | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    schedule: Schedule | null;
  }>({ isOpen: false, schedule: null });

  const agents = agentsData?.items ?? [];
  const agentMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of agents) {
      map.set(a.agentId, a.name);
    }
    return map;
  }, [agents]);

  const conversationMap = useMemo(() => {
    const map = new Map<string, { name?: string }>();
    for (const c of conversationsData?.items ?? []) {
      map.set(c.conversationId, { name: c.name });
    }
    return map;
  }, [conversationsData]);

  const schedules = useMemo(() => {
    const items = data?.items ?? [];
    if (statusFilter === 'all') return items;
    return items.filter((s) => s.status === statusFilter);
  }, [data, statusFilter]);

  const handleToggleStatus = async (schedule: Schedule) => {
    const newStatus: 'active' | 'paused' =
      schedule.status === 'active' ? 'paused' : 'active';
    try {
      await updateSchedule.mutateAsync({
        scheduleId: schedule.scheduleId,
        input: { status: newStatus },
      });
      success(`Schedule ${newStatus === 'paused' ? 'paused' : 'resumed'} successfully`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update schedule');
    }
  };

  const handleReactivate = async (schedule: Schedule) => {
    try {
      await updateSchedule.mutateAsync({
        scheduleId: schedule.scheduleId,
        input: { status: 'active' },
      });
      success('Schedule reactivated successfully');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to reactivate schedule');
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.schedule) return;
    try {
      await deleteSchedule.mutateAsync(deleteModal.schedule.scheduleId);
      success('Schedule deleted successfully');
      setDeleteModal({ isOpen: false, schedule: null });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete schedule');
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="w-48">
          <Select
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(e: { target: { value: string } }) =>
              setStatusFilter(e.target.value)
            }
          />
        </div>
        <Button onClick={() => setCreateModal(true)}>Create Schedule</Button>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      ) : schedules.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {statusFilter !== 'all'
            ? 'No schedules match this filter.'
            : 'No schedules found. Create your first schedule to automate agent runs.'}
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden divide-y divide-border">
          {schedules.map((schedule) => (
            <div
              key={schedule.scheduleId}
              className="flex items-center justify-between px-4 py-3"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-foreground truncate">
                    {schedule.name}
                  </p>
                  <Badge variant={schedule.scheduleType === 'one_time' ? 'purple' : 'teal'}>
                    {schedule.scheduleType === 'one_time' ? 'One-time' : 'Recurring'}
                  </Badge>
                  <Badge variant={getStatusVariant(schedule.status)}>
                    {schedule.status}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                  {!agentId && (
                    <span>Agent: {agentMap.get(schedule.agentId) || schedule.agentId}</span>
                  )}
                  {schedule.conversationId && (
                    <span>
                      Conversation:{' '}
                      <Link
                        to={routes.operator.chat(schedule.conversationId)}
                        className="text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {conversationMap.get(schedule.conversationId)?.name ?? schedule.conversationId}
                      </Link>
                    </span>
                  )}
                  <span>{formatExpression(schedule)}</span>
                  {schedule.nextTriggerAt && (
                    <span>Next: {formatDate(schedule.nextTriggerAt)}</span>
                  )}
                  <span>Runs: {schedule.executionCount}</span>
                  {schedule.maxExecutions && (
                    <span>/ {schedule.maxExecutions}</span>
                  )}
                </div>
                {schedule.status === 'failed' && schedule.failedReason && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    Failed: {schedule.failedReason}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 ml-4 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditSchedule(schedule)}
                >
                  Edit
                </Button>
                {(schedule.status === 'active' || schedule.status === 'paused') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleStatus(schedule)}
                    disabled={updateSchedule.isPending}
                  >
                    {schedule.status === 'active' ? 'Pause' : 'Resume'}
                  </Button>
                )}
                {schedule.status === 'failed' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleReactivate(schedule)}
                    disabled={updateSchedule.isPending}
                  >
                    Reactivate
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDeleteModal({ isOpen: true, schedule })}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30"
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create modal */}
      <CreateScheduleModal
        isOpen={createModal}
        onClose={() => setCreateModal(false)}
        agentId={agentId}
      />

      {/* Edit modal */}
      {editSchedule && (
        <CreateScheduleModal
          isOpen={true}
          onClose={() => setEditSchedule(null)}
          agentId={agentId}
          schedule={editSchedule}
        />
      )}

      {/* Delete confirmation */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, schedule: null })}
        onConfirm={handleDelete}
        title="Delete Schedule"
        message={`Are you sure you want to delete "${deleteModal.schedule?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteSchedule.isPending}
      />
    </div>
  );
}
